/**
 * Course snapshot written by content/src/course/scripts/package.mjs:
 * data/json/<lang>/courses/{crs001.json,scenes/<id>/item.json,units/<id>/item.json}.
 */
import { existsSync, readFileSync, readdirSync, statSync } from "node:fs";
import { join } from "node:path";
import { getContentRepoJsonRoot, getDataJsonRoot } from "../../lib/paths.js";

import { courseLocale, courseLocaleRevision } from "./locales.js";
import { mainCourseIsAvailable } from "../../lib/main-course-availability.js";
import { SnapshotCache } from './snapshot-cache.js';

const COURSE_ID = "crs001";
const cache = new SnapshotCache();

// Parsed snapshot files, kept outside development (like the bundles below) so a course's documents are
// read and their English copy prepared once for all its interfaces (locales.js). Hydration never
// modifies them.
const parsedFiles = new Map();

function readJson(file) {
  if (process.env.NODE_ENV === "development") return JSON.parse(readFileSync(file, "utf8"));
  if (!parsedFiles.has(file)) parsedFiles.set(file, JSON.parse(readFileSync(file, "utf8")));
  return parsedFiles.get(file);
}

function firstExisting(paths) {
  return paths.find((file) => file && existsSync(file)) || "";
}

function snapshotPaths(lang) {
  if (process.env.LL_COURSE_RELEASE_ROOT) {
    const data = join(process.env.LL_COURSE_RELEASE_ROOT, lang, 'courses');
    if (!existsSync(join(data, `${COURSE_ID}.json`))) return {};
    return { course: join(data, `${COURSE_ID}.json`), scenes: join(data, 'scenes'), units: join(data, 'units') };
  }
  const data = join(getDataJsonRoot(), lang, "courses");
  const sot = join(getContentRepoJsonRoot() || "", lang, "sot");
  return {
    course: firstExisting([join(data, `${COURSE_ID}.json`), join(sot, "courses", COURSE_ID, "item.json")]),
    scenes: firstExisting([join(data, "scenes"), join(sot, "course-scenes")]),
    units: firstExisting([join(data, "units"), join(sot, "course-units")]),
  };
}

/** Audio refreshes can update an item without rebuilding the course manifest. */
function snapshotRevision(paths) {
  const files = [paths.course];
  for (const directory of [paths.scenes, paths.units]) {
    if (!directory) continue;
    for (const id of readdirSync(directory).sort()) {
      const file = join(directory, id, "item.json");
      if (existsSync(file)) files.push(file);
    }
  }
  return files.filter(Boolean).map((file) => {
    const stat = statSync(file);
    return `${file}:${stat.mtimeMs}:${stat.size}`;
  }).join("|");
}

const CAST_PLACEHOLDER = /\{([a-z][a-z0-9-]*)\}/g;

/** `{granddaughter}` and the other cast ids become that language's names. `{name}` stays for the learner. */
function fillCastNames(value, names) {
  if (typeof value === "string") {
    if (!value.includes("{")) return value;
    return value.replace(CAST_PLACEHOLDER, (all, id) => (id === "name" ? all : names[id] || all));
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = fillCastNames(value[i], names);
    return value;
  }
  if (value && typeof value === "object") {
    for (const key of Object.keys(value)) value[key] = fillCastNames(value[key], names);
    return value;
  }
  return value;
}

function castNames(course) {
  const names = {};
  for (const [id, person] of Object.entries(course.language?.cast || {})) {
    if (person?.name) names[id] = person.name;
  }
  return names;
}

function readItems(dir) {
  const out = {};
  if (!dir) return out;
  for (const id of readdirSync(dir)) {
    const file = join(dir, id, "item.json");
    if (!existsSync(file)) continue;
    const item = readJson(file);
    out[item.id] = item;
  }
  return out;
}

function setDictionary(set, local = {}) {
  const out = Object.fromEntries((set.items || []).map((item) => {
    const entry = local.items?.[item.id] || {};
    return [item.id, { word: entry.word || "", en: entry.en || item.en || "" }];
  }));
  for (const item of local.extraRows || []) out[item.id] = { word: item.word || "", en: item.en || "" };
  return out;
}

/** Concept id → { word, en } in the course language, for tap-to-look-up. */
function dictionary(course) {
  return Object.assign({}, ...(course.sets || []).map((set) => setDictionary(set, course.language?.sets?.[set.id])));
}

function orderedScenes(course) {
  return course.modules.flatMap((mod, moduleIndex) =>
    mod.scenes.map((scene, sceneIndex) => ({ ...scene, moduleIndex, sceneIndex, moduleId: mod.id })),
  );
}

/** One course language's snapshot; null when that language has none. */
function snapshotCacheKey(code, uiLang) { return `${process.env.LL_COURSE_RELEASE_ROOT || ''}:${code}:${uiLang}`; }

function localeRootFor(paths) { return paths.course ? join(paths.course, "..") : ""; }

/** Learning the interface language itself (a German interface on the German course): translations in English. */
function glossLanguage(code, uiLang) {
  return uiLang === code ? "en" : uiLang;
}

export function loadCourseBundle(lang = "lt", uiLang = "en") {
  const code = String(lang).trim().toLowerCase();
  if (!mainCourseIsAvailable(code)) return null;
  const key = snapshotCacheKey(code, uiLang);
  const development = process.env.NODE_ENV === "development";
  if (!development && cache.has(key)) return cache.get(key).bundle;
  const paths = /^[a-z]{2,3}$/.test(code) ? snapshotPaths(code) : {};
  const localeRoot = localeRootFor(paths);
  const revision = development ? snapshotRevision(paths) + courseLocaleRevision(localeRoot, uiLang) : "";
  const cached = cache.get(key);
  if (cached && cached.revision === revision) return cached.bundle;
  if (!paths.course || !paths.scenes) {
    cache.set(key, { revision, bundle: null });
    return null;
  }
  const gloss = glossLanguage(code, uiLang);
  const locale = courseLocale(localeRoot, uiLang, { gloss });
  const course = locale.hydrateSource(readJson(paths.course), 'course');
  const names = castNames(course);
  fillCastNames(course, names);
  const scenes = Object.fromEntries(Object.entries(readItems(paths.scenes)).map(([id, item]) => [id, locale.hydrateSource(item, 'scene', id)]));
  const units = Object.fromEntries(Object.entries(readItems(paths.units)).map(([id, item]) => [id, locale.hydrateSource(item, 'unit', id)]));
  fillCastNames(scenes, names);
  fillCastNames(units, names);
  const bundle = {
    lang: code,
    uiLang,
    glossLang: gloss,
    localize: locale.resolve,
    lookups: locale.lookups,
    course,
    scenes,
    units,
    order: orderedScenes(course),
    dictionary: dictionary(course),
  };
  cache.set(key, { revision, bundle });
  return bundle;
}

export function courseHasContent(targetCode) {
  return !!loadCourseBundle(targetCode);
}

/** Catalogue presence only: avoid hydrating every course to draw homepage links. */
export function courseHasSnapshot(targetCode) {
  const code = String(targetCode || "").trim().toLowerCase();
  if (!/^[a-z]{2,3}$/.test(code) || !mainCourseIsAvailable(code)) return false;
  const paths = snapshotPaths(code);
  return Boolean(paths.course && paths.scenes);
}

export function sceneByUrlSlug(bundle, urlSlug) {
  const want = String(urlSlug || "").trim().toLowerCase();
  return Object.values(bundle.scenes).find((scene) => scene.urlSlug === want || scene.legacyUrlSlugs?.includes(want) || scene.id === want) || null;
}

/** Unit slugs below a scene: words and grammar after every scene, the reviews after a module's last scene. */
export const UNIT_SLUGS = Object.freeze({
  words: "words",
  grammar: "grammar",
  "vocab-review": "review-words",
  "grammar-review": "review-grammar",
  story: "story",
});

/** The units shown after a scene, in course order. */
export function unitsAfterScene(bundle, scene) {
  const mod = bundle.course.modules.find((item) => item.id === scene.moduleId);
  const list = mod?.units || [];
  const last = mod?.scenes[mod.scenes.length - 1]?.id === scene.id;
  return list.filter((unit) => unit.after === scene.id || (last && !unit.after));
}

export function unitBySlug(bundle, scene, slug) {
  const ref = unitsAfterScene(bundle, scene).find((unit) => UNIT_SLUGS[unit.type] === slug);
  return ref ? bundle.units[ref.id] || null : null;
}

/** Where a unit lives: the scene it follows (module units follow the module's last scene). */
export function sceneOfUnit(bundle, unitRef, mod) {
  const id = unitRef.after || mod.scenes[mod.scenes.length - 1]?.id;
  return bundle.order.find((scene) => scene.id === id) || null;
}

/** The route key of a module's own pages (reviews, bonus story): `module-1` for the first module. */
export function moduleRouteSlug(bundle, mod) {
  return `module-${bundle.course.modules.indexOf(mod) + 1}`;
}

export function moduleByRouteSlug(bundle, slug) {
  const n = /^module-(\d+)$/.exec(String(slug || ""))?.[1];
  return n ? bundle.course.modules[Number(n) - 1] || null : null;
}

/**
 * A unit's URL parts. Units after a scene live under that scene; the reviews and the bonus
 * story belong to the whole module and live under `module-N`.
 */
export function unitRoute(bundle, unitRef, mod) {
  const sceneSlug = UNIT_SLUGS[unitRef.type];
  if (!unitRef.after) return { slug: moduleRouteSlug(bundle, mod), sceneSlug };
  const scene = sceneOfUnit(bundle, unitRef, mod);
  return scene ? { slug: scene.urlSlug, sceneSlug } : null;
}
